export const OPERATING_LOCATIONS = {
  US: "US",
  IND: "IND",
  BGD: "BGD",
  DEFAULT: "US"
};

export const LOCATION_CONFIG = {
  [OPERATING_LOCATIONS.US]: {
    operatingLocationCode: OPERATING_LOCATIONS.US,
    country: "United States",
    countryIso2: "US",
    countryCode: "+1",
    currency: "USD",
    locale: "en-US",
    timeZone: "America/New_York",
    phonePlaceholder: "1234567890",
    phoneNationalLength: 10,
    postalCodeLabel: "Zip Code"
  },
  [OPERATING_LOCATIONS.IND]: {
    operatingLocationCode: OPERATING_LOCATIONS.IND,
    country: "India",
    countryIso2: "IN",
    countryCode: "+91",
    currency: "INR",
    locale: "en-IN",
    timeZone: "Asia/Kolkata",
    phonePlaceholder: "9876543210",
    phoneNationalLength: 10,
    postalCodeLabel: "Pin Code"
  },
  [OPERATING_LOCATIONS.BGD]: {
    operatingLocationCode: OPERATING_LOCATIONS.BGD,
    country: "Bangladesh",
    countryIso2: "BD",
    countryCode: "+880",
    currency: "BDT",
    locale: "en-BD",
    timeZone: "Asia/Dhaka",
    phonePlaceholder: "1712345678",
    phoneNationalLength: 10,
    postalCodeLabel: "Postal Code"
  }
};

export const COUNTRY_OPTIONS = Object.values(LOCATION_CONFIG).map((item) => ({
  value: item.country,
  label: item.country,
  iso2: item.countryIso2,
  countryCode: item.countryCode,
  currency: item.currency,
  operatingLocationCode: item.operatingLocationCode
}));

export const COUNTRY_CODE_ALIASES = {
  India: OPERATING_LOCATIONS.IND,
  IN: OPERATING_LOCATIONS.IND,
  IND: OPERATING_LOCATIONS.IND,
  USA: OPERATING_LOCATIONS.US,
  US: OPERATING_LOCATIONS.US,
  "United States": OPERATING_LOCATIONS.US,
  Bangladesh: OPERATING_LOCATIONS.BGD,
  BD: OPERATING_LOCATIONS.BGD,
  BGD: OPERATING_LOCATIONS.BGD
};

export const TIMEZONE_ABBREVIATIONS = {
  "America/New_York": "EST",
  "America/Chicago": "CST",
  "America/Denver": "MST",
  "America/Los_Angeles": "PST",
  "America/Phoenix": "MST",
  "America/Anchorage": "AKST",
  "America/Honolulu": "HST",
  "America/Toronto": "EST",
  "America/Vancouver": "PST",
  "Europe/London": "GMT",
  "Europe/Paris": "CET",
  "Europe/Berlin": "CET",
  "Europe/Rome": "CET",
  "Europe/Madrid": "CET",
  "Asia/Kolkata": "IST",
  "Asia/Dhaka": "BST",
  "Asia/Dubai": "GST",
  "Asia/Singapore": "SGT",
  "Asia/Tokyo": "JST",
  "Asia/Shanghai": "CST",
  "Australia/Sydney": "AEST",
  "Australia/Perth": "AWST",
  "Pacific/Auckland": "NZST"
};

export const normalizeOperatingLocationCode = (value) => {
  const code = String(value || "").trim().toUpperCase();
  const digits = code.replace(/\D/g, "");
  if (digits === "1" || digits === "001") return OPERATING_LOCATIONS.US;
  if (digits === "91" || digits === "0091") return OPERATING_LOCATIONS.IND;
  if (digits === "880" || digits === "00880") return OPERATING_LOCATIONS.BGD;
  if (code === "IN" || code === "INDIA") return OPERATING_LOCATIONS.IND;
  if (code === "BD" || code === "BANGLADESH") return OPERATING_LOCATIONS.BGD;
  if (code === "INR") return OPERATING_LOCATIONS.IND;
  if (code === "BDT") return OPERATING_LOCATIONS.BGD;
  if (code === "USD") return OPERATING_LOCATIONS.US;
  if (code === "USA" || code === "UNITED_STATES" || code === "UNITED STATES") return OPERATING_LOCATIONS.US;
  return LOCATION_CONFIG[code] ? code : OPERATING_LOCATIONS.DEFAULT;
};

export const getOperatingLocationCode = (companyOrCode) => {
  if (typeof companyOrCode === "string") {
    return normalizeOperatingLocationCode(companyOrCode);
  }

  return normalizeOperatingLocationCode(
    companyOrCode?.operatingLocation?.code
      || companyOrCode?.operatingLocationCode
      || companyOrCode?.countryCode
      || OPERATING_LOCATIONS.DEFAULT
  );
};

export const getLocationConfig = (companyOrCode) => (
  LOCATION_CONFIG[getOperatingLocationCode(companyOrCode)] || LOCATION_CONFIG[OPERATING_LOCATIONS.DEFAULT]
);

export const getDefaultCountryCode = (companyOrCode) => getLocationConfig(companyOrCode).countryCode;

export const getCurrencyCode = (companyOrCode) => getLocationConfig(companyOrCode).currency;

export const getPhonePlaceholder = (companyOrCode) => getLocationConfig(companyOrCode).phonePlaceholder;

export const getPhoneNationalLength = (companyOrCode) => getLocationConfig(companyOrCode).phoneNationalLength;

export const getPostalCodeLabel = (companyOrCode) => getLocationConfig(companyOrCode).postalCodeLabel;

export const getCountryOperatingLocationCode = (country) => (
  COUNTRY_CODE_ALIASES[country] || normalizeOperatingLocationCode(country)
);

export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getCompanyTimeZone = (companyOrCode) => (
  companyOrCode?.operatingLocation?.zoneId
    || companyOrCode?.timeZone
    || getLocationConfig(companyOrCode).timeZone
    || getBrowserTimeZone()
);

const extractDigits = (value) => String(value || "").replace(/\D/g, "");

export const normalizePhoneNumber = (phone, companyOrCode) => {
  const location = getLocationConfig(companyOrCode);
  let digits = extractDigits(phone);
  const callingCode = extractDigits(location.countryCode);
  const nationalLength = location.phoneNationalLength;

  if (digits.startsWith(`00${callingCode}`)) {
    digits = digits.slice(callingCode.length + 2);
  } else if (digits.startsWith(callingCode) && digits.length === nationalLength + callingCode.length) {
    digits = digits.slice(callingCode.length);
  }

  return digits;
};

export const isValidPhoneNumber = (phone, companyOrCode) => {
  const digits = normalizePhoneNumber(phone, companyOrCode);
  return !digits || digits.length === getPhoneNationalLength(companyOrCode);
};

export const formatNationalPhoneNumber = (phone, companyOrCode) => {
  const location = getLocationConfig(companyOrCode);
  const digits = normalizePhoneNumber(phone, companyOrCode);
  if (!digits || digits.length !== location.phoneNationalLength) return String(phone || "");

  if (location.operatingLocationCode === OPERATING_LOCATIONS.US) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (location.operatingLocationCode === OPERATING_LOCATIONS.IND) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  if (location.operatingLocationCode === OPERATING_LOCATIONS.BGD) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  return digits;
};

export const normalizeCountryCallingCode = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return trimmed;
  return `+${digits}`;
};

export const formatPhoneNumber = (phone, companyOrCode) => {
  if (!phone) return "-";

  const location = getLocationConfig(companyOrCode);
  let digits = extractDigits(phone);

  if (location.operatingLocationCode === OPERATING_LOCATIONS.US) {
    digits = digits.replace(/^001/, "").replace(/^1/, "");
    if (digits.length >= 10) digits = digits.slice(-10);
    if (digits.length === 10) {
      return `${location.countryCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
  }

  if (location.operatingLocationCode === OPERATING_LOCATIONS.IND) {
    digits = digits.replace(/^0091/, "").replace(/^91/, "");
    if (digits.length >= 10) digits = digits.slice(-10);
    if (digits.length === 10) {
      return `${location.countryCode} ${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
  }

  if (location.operatingLocationCode === OPERATING_LOCATIONS.BGD) {
    digits = digits.replace(/^00880/, "").replace(/^880/, "");
    return `${location.countryCode} ${digits}`;
  }

  return `${location.countryCode} ${digits || phone}`.trim();
};

export const formatCurrency = (amount, companyOrCode, options = {}) => {
  if (amount === null || amount === undefined || amount === "") return "-";

  const numericValue = Number(amount);
  if (Number.isNaN(numericValue)) return amount;

  const location = getLocationConfig(companyOrCode);
  return new Intl.NumberFormat(location.locale, {
    style: "currency",
    currency: options.currency || location.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(numericValue);
};

export const getTimezoneAbbreviation = (zoneId) => {
  if (!zoneId) return "";
  if (TIMEZONE_ABBREVIATIONS[zoneId]) return TIMEZONE_ABBREVIATIONS[zoneId];
  return zoneId.split("/").pop().replace(/_/g, " ");
};

export const getDSTAwareTimezoneAbbreviation = (zoneId) => {
  if (!zoneId) return "";

  try {
    const value = new Date().toLocaleString("en-US", {
      timeZone: zoneId,
      timeZoneName: "short"
    });
    const match = value.match(/([A-Z]{2,5})$/);
    return match?.[1] || getTimezoneAbbreviation(zoneId);
  } catch (error) {
    return getTimezoneAbbreviation(zoneId);
  }
};

const locationConfig = {
  OPERATING_LOCATIONS,
  LOCATION_CONFIG,
  COUNTRY_OPTIONS,
  COUNTRY_CODE_ALIASES,
  TIMEZONE_ABBREVIATIONS,
  normalizeOperatingLocationCode,
  getOperatingLocationCode,
  getLocationConfig,
  getDefaultCountryCode,
  getCurrencyCode,
  getPhonePlaceholder,
  getPhoneNationalLength,
  getPostalCodeLabel,
  getCountryOperatingLocationCode,
  getBrowserTimeZone,
  getCompanyTimeZone,
  normalizeCountryCallingCode,
  normalizePhoneNumber,
  isValidPhoneNumber,
  formatNationalPhoneNumber,
  formatPhoneNumber,
  formatCurrency,
  getTimezoneAbbreviation,
  getDSTAwareTimezoneAbbreviation
};

export default locationConfig;
